import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import View from '@play-co/timestep-core/lib/ui/View';
import PopupBasicLazy from 'src/game/components/popups/PopupBasicLazy';
import buttons from 'src/lib/ui/config/buttons';
import { ImageScaleView, MovieClip, animate } from '@play-co/timestep-core/ui';
import { AssetGroup } from '@play-co/timestep-core';
import { Feature } from 'src/replicant/analytics';
import i18n from 'src/lib/i18n/i18n';
import StateObserver from 'src/StateObserver';
import { getScreenDimensions } from 'src/lib/utils';
import { BuddiesRibbon } from '../components/BuddiesRibbon';
import sounds from 'src/lib/sounds';
import { BuddiesBackground } from '../components/BuddiesBackground';
import { BuddiesRanking } from '../components/BuddiesRanking';
import { BuddiesGemsReward } from '../components/BuddiesGemsReward';
import { BuddiesRankingEntryParams } from '../components/BuddiesRankingEntry';
import ConfettiOverlay from 'src/game/minigame/match2-timestep/components/ConfettiOverlay';

type CloseHandler = () => void;

export default class PopupBuddiesReward extends PopupBasicLazy {
  private background: BuddiesBackground;
  private congratulations: ImageScaleView;
  private ribbon: BuddiesRibbon;
  private gems: BuddiesGemsReward;
  private ranking: BuddiesRanking;
  private claimButton: ButtonScaleViewWithText;
  private confetti: MovieClip;
  private confettiBg: ConfettiOverlay;
  private close: CloseHandler;

  constructor(opts: { superview: View; close: CloseHandler }) {
    const width = opts.superview.style.width;
    const height = getScreenDimensions().height;

    super({
      superview: opts.superview,
      skipBackground: true,
      skipTitle: true,
      width,
      height,
      close: () => opts.close(),
      tracking: {
        feature: Feature.BUDDIES._,
        $subFeature: Feature.BUDDIES.POPUP_INFO,
      },
      zIndex: 10000,
    });

    this.background = new BuddiesBackground({
      superview: this.box,
      width: width,
      height: height,
      x: width / 2,
      y: height / 2,
      centerOnOrigin: true,
    });

    // This popup should not have a background
    this.bg.updateOpts({
      backgroundColor: 'rgba(0,0,0,0)',
    });

    this.close = opts.close;

    this.confettiBg = new ConfettiOverlay({
      superview: this.box,
    });

    this.gems = new BuddiesGemsReward({
      superview: this.box,
      x: width / 2,
      y: height / 2,
      centerOnOrigin: true,
    });

    this.ranking = new BuddiesRanking({
      superview: this.box,
      x: width / 2,
      y: height / 2 + 200,
      centerOnOrigin: true,
      visible: false,
    });

    this.congratulations = new ImageScaleView({
      superview: this.box,
      x: width / 2,
      y: height / 2 - 550,
      width: 631,
      height: 105,
      centerOnOrigin: true,
      visible: false,
    });

    this.ribbon = new BuddiesRibbon({
      superview: this.box,
      x: width / 2,
      y: height / 2 - 300,
      centerOnOrigin: true,
      visible: false,
    });

    this.buttonClose.updateOpts({
      visible: false,
    });

    this.confetti = new MovieClip({
      superview: this.box,
      url: 'assets/ui/match2/movieclips/level_complete_stars',
      x: width / 2,
      y: height / 2 + 200,
    });

    this.claimButton = new ButtonScaleViewWithText({
      superview: this.box,
      ...buttons.secondary,
      width: 317,
      height: 95,
      fontSize: 40,
      labelOffsetY: -2,
      centerOnOrigin: true,
      x: width / 2,
      y: height / 2 + 500,
      font: 'Body',
      tracking: {
        feature: Feature.BUDDIES._,
        $subFeature: Feature.BUDDIES.BUTTON_START,
      },
      localeText: () => i18n('match2.buddies.rewardPopup.claim'),
      onClick: async () => this.claim(),
    });
  }

  public getAssetsGroup() {
    return new AssetGroup([
      ...BuddiesGemsReward.getAssets(),
      ...AssetGroup.constructURLs([
        'assets/ui/buddies/common',
        'assets/ui/buddies/popupReward',
      ]),
    ]);
  }

  public async init(args: {
    baseGems: number;
    totalGems: number;
    multiplier: number;
    ranking: BuddiesRankingEntryParams[];
  }) {
    super.init({});
    const locale = StateObserver.getLocale();
    this.congratulations.updateOpts({
      image: `assets/ui/buddies/popupReward/congratulations_${locale}.png`,
    });
    this.ribbon.setText(i18n('match2.buddies.rewardPopup.reward'));
    this.claimButton.hide();
    this.ranking.setup(args.ranking);
    this.gems.setup(args);
  }

  public async fadeIn() {
    this.overlay.show();
    this.attachRoot();
    this.box.hide();
    this.bg.updateOpts({ opacity: 0 });
    await this.loadAssets();
    this.overlay.hide();
    this.box.show();
    await this.showAnimated();
  }

  public async fadeOut() {
    this.overlay.show();
    animate(this.box)
      .clear()
      .wait(0)
      .then({ opacity: 0 }, 200, animate.easeOut)
      .toPromise()
      .then(() => {
        this.detachRoot();
        this.overlay.hide();
      });
  }

  private async showAnimated() {
    // Fade in the background
    this.background.updateOpts({ opacity: 0 });
    animate(this.background).then({ opacity: 1 }, 500);

    await animate(this).wait(200).toPromise();

    sounds.sfx.play('tada');

    // Show the title subtitle with animation
    this.congratulations.updateOpts({
      offsetY: -200,
      opacity: 0,
      visible: true,
    });
    animate(this.congratulations).then(
      { offsetY: 0, opacity: 1 },
      300,
      animate.easeOutBack,
    );

    await animate(this).wait(100).toPromise();

    // Show the ribbon subtitle with animation
    this.ribbon.updateOpts({
      scale: 0,
      visible: true,
    });
    animate(this.ribbon).then({ scale: 1 }, 300, animate.easeOutBack);

    await animate(this).wait(100).toPromise();

    // Show gems rewards animation
    await this.gems.showAnimated();

    // Play all the confetti at once
    this.confetti.play(`staranim_confetti`);
    this.confettiBg.show();

    // Rearrange elements for ranking reveal
    animate(this.ribbon).then({ offsetY: -130 }, 500).toPromise();
    animate(this.gems).then({ offsetY: -190 }, 500).toPromise();
    animate(this.ranking).then({ offsetY: -200 }, 500).toPromise();

    // Show ranking with animation
    await this.ranking.showAnimated();

    // Finally, show the claim button
    this.claimButton.updateOpts({
      scale: 0,
      visible: true,
    });
    await animate(this.claimButton)
      .then({ scale: 1 }, 300, animate.easeOutBack)
      .toPromise();
  }

  private async claim() {
    sounds.sfx.play('win');
    this.claimButton.hide();
    this.close();
  }
}
